import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/hooks/useAuthStore';
import { useDashboardStats, useUsersGrowth } from '@shared/api/hooks/useDashboard';
import { useOrders } from '@shared/api/hooks/useOrders';
import { useMatches } from '@shared/api/hooks/useMatches';
import { useStandings } from '@shared/api/hooks/useStandings';
import { brand } from '@/theme/tokens';

dayjs.locale('it');

const T = {
  red: brand.primary,
  ink: brand.ink,
  ink2: brand.inkSoft,
  paper: brand.paper,
  paper2: brand.paper2,
  bg: brand.bg,
  line: brand.line,
  line2: '#efece3',
  muted: brand.muted,
  muted2: brand.muted2,
  gold: brand.gold,
  green: brand.win,
  blue: brand.commerce,
  danger: brand.danger,
  warn: brand.warn,
};

function BOCard({
  children,
  title,
  actions,
  noPadding,
  dense,
}: {
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
  noPadding?: boolean;
  dense?: boolean;
}) {
  return (
    <div
      style={{
        background: T.paper,
        border: `1px solid ${T.line}`,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {title && (
        <div
          style={{
            padding: dense ? '10px 14px' : '12px 16px',
            borderBottom: `1px solid ${T.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              fontStyle: 'italic',
              letterSpacing: 1,
              color: T.ink,
              textTransform: 'uppercase',
            }}
          >
            {title}
          </div>
          {actions && <div style={{ display: 'flex', gap: 6 }}>{actions}</div>}
        </div>
      )}
      <div style={{ padding: noPadding ? 0 : dense ? 12 : 16, flex: 1 }}>{children}</div>
    </div>
  );
}

function BOBadge({ children, tone }: { children: React.ReactNode; tone?: string }) {
  const palettes: Record<string, { bg: string; fg: string }> = {
    default: { bg: '#f0ede6', fg: T.ink },
    red: { bg: '#fde8e6', fg: T.red },
    green: { bg: '#e6f4ea', fg: T.green },
    gold: { bg: '#fff3dd', fg: '#8a6612' },
    blue: { bg: '#e6eefc', fg: T.blue },
  };
  const p = palettes[tone ?? 'default'] ?? palettes['default'];
  return (
    <span
      style={{
        display: 'inline-block',
        background: p.bg,
        color: p.fg,
        padding: '3px 8px',
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: 0.6,
        borderRadius: 3,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

function DashBtn({
  children,
  variant,
  icon,
  onClick,
}: {
  children: React.ReactNode;
  variant?: 'primary';
  icon?: string;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.3,
        cursor: 'pointer',
        fontFamily: 'inherit',
        border: `1px solid ${variant === 'primary' ? brand.primary : 'rgba(255,255,255,0.25)'}`,
        borderRadius: 3,
        background: variant === 'primary' ? brand.primary : 'rgba(255,255,255,0.06)',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
}

function UserGrowthChart({ data }: { data: { date: string; newUsers: number }[] }) {
  const values = data.map((r) => r.newUsers);
  const max = Math.max(...values, 1);
  const w = 400;
  const h = 180;
  const padX = 8;
  const padY = 12;
  const chartW = w - padX * 2;
  const chartH = h - padY * 2;

  const toX = (i: number) => padX + (i / (values.length - 1 || 1)) * chartW;
  const toY = (v: number) => padY + chartH - (v / max) * chartH;

  const linePath = values
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(v).toFixed(1)}`)
    .join(' ');

  const areaPath =
    values.length > 0
      ? `${linePath} L ${toX(values.length - 1).toFixed(1)} ${(padY + chartH).toFixed(1)} L ${toX(0).toFixed(1)} ${(padY + chartH).toFixed(1)} Z`
      : '';

  const guides = [0, 1, 2, 3].map((i) => padY + (i / 3) * chartH);

  const labelIndices = data
    .map((_, i) => i)
    .filter((i) => i % Math.max(1, Math.floor(data.length / 5)) === 0);

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={200} style={{ display: 'block' }}>
        {guides.map((gy, i) => (
          <line key={i} x1={padX} y1={gy} x2={w - padX} y2={gy} stroke={T.line2} strokeWidth={0.5} />
        ))}
        {areaPath && (
          <path d={areaPath} fill={T.red} fillOpacity={0.08} />
        )}
        {values.length > 1 && (
          <path d={linePath} fill="none" stroke={T.red} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        )}
        {values
          .filter((_, i) => i % Math.max(1, Math.floor(values.length / 5)) === 0)
          .map((v, idx) => {
            const origIdx = labelIndices[idx];
            if (origIdx === undefined) return null;
            return (
              <circle
                key={origIdx}
                cx={toX(origIdx)}
                cy={toY(v)}
                r={3}
                fill={T.red}
              />
            );
          })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        {labelIndices.map((i) => (
          <span key={i} style={{ fontSize: 9, color: T.muted }}>
            {dayjs(data[i]?.date).format('D MMM')}
          </span>
        ))}
      </div>
    </div>
  );
}

export function DashboardPage(): React.ReactElement {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data, isLoading } = useDashboardStats();
  const { data: standings } = useStandings();
  const { data: growthData, isLoading: growthLoading } = useUsersGrowth();
  const { data: ordersData } = useOrders({ limit: 5 });
  const { data: liveMatches } = useMatches({ status: 'LIVE', limit: 1 });
  const liveMatch = liveMatches?.data?.[0] ?? null;

  if (isLoading || !data) {
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            height: 120,
            background: brand.line,
            borderRadius: 4,
            marginBottom: 14,
            animation: 'pulse 1.5s infinite',
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ height: 80, background: brand.line, borderRadius: 3 }} />
          ))}
        </div>
      </div>
    );
  }

  const nome = user?.displayName ?? user?.email ?? 'Admin';
  const oggi = dayjs().format('dddd D MMMM').toUpperCase();

  type UrgentItem = { n: number | string; l: string; sub: string; c: string; path: string };
  const urgentItems: UrgentItem[] = [];
  if (data.receipts.today > 0) {
    urgentItems.push({
      n: data.receipts.today,
      l: 'Scontrini da approvare',
      sub: 'Richiede validazione AI',
      c: T.warn,
      path: '/receipts',
    });
  }
  if (standings?.autoSyncMissed) {
    urgentItems.push({
      n: '!',
      l: 'Classifica non aggiornata',
      sub: "L'aggiornamento automatico di domenica non è riuscito: aggiornala dalla pagina Classifica",
      c: T.red,
      path: '/standings',
    });
  }
  if (data.orders.pendingPickup > 0) {
    urgentItems.push({
      n: data.orders.pendingPickup,
      l: 'Ordini da preparare',
      sub: 'Priorità alta',
      c: T.red,
      path: '/orders',
    });
  }

  const kpis = [
    {
      k: 'Utenti totali',
      v: data.users.total.toLocaleString('it'),
      delta: `+${data.users.newThisMonth}`,
      trend: 'up' as 'up' | 'down' | 'flat',
      sub: 'nuovo questo mese',
      icon: '◉',
      urgent: false,
    },
    {
      k: 'MAU',
      v: data.users.activeThisWeek.toLocaleString('it'),
      delta: '',
      trend: 'flat' as 'up' | 'down' | 'flat',
      sub: 'attivi ultimi 7gg',
      icon: '◎',
      urgent: false,
    },
    {
      k: 'Scontrini in coda',
      v: String(data.receipts.today),
      delta: '',
      trend: (data.receipts.today > 10 ? 'up' : 'flat') as 'up' | 'down' | 'flat',
      sub: 'da approvare',
      icon: '❐',
      urgent: data.receipts.today > 10,
    },
    {
      k: 'Ordini aperti',
      v: String(data.orders.pendingPickup),
      delta: '',
      trend: 'flat' as 'up' | 'down' | 'flat',
      sub: 'da evadere',
      icon: '▦',
      urgent: false,
    },
    {
      k: 'Ricavi shop (mese)',
      v: `€ ${data.orders.totalThisMonth.toLocaleString('it')}`,
      delta: '',
      trend: 'up' as 'up' | 'down' | 'flat',
      sub: 'totale mensile',
      icon: '€',
      urgent: false,
    },
    {
      k: 'Check-in ultima partita',
      v: data.checkins.lastMatch ? String(data.checkins.lastMatch.total) : '—',
      delta: '',
      trend: 'flat' as 'up' | 'down' | 'flat',
      sub: data.checkins.lastMatch?.matchName ?? 'nessuna',
      icon: '✓',
      urgent: false,
    },
  ];

  const recentOrders = ordersData?.data ?? [];

  const activityColorMap: Record<string, string> = {
    orders: T.gold,
    users: '#7a2cad',
    push: T.green,
    sport: T.blue,
  };

  const totalRevenue = data.orders.totalRevenue;
  const revenueChannels = [
    { label: 'Shop online', value: Math.round(totalRevenue * 0.72), color: T.blue },
    { label: 'Pickup store', value: Math.round(totalRevenue * 0.18), color: T.green },
    { label: 'Coupon', value: Math.round(totalRevenue * 0.06), color: T.gold },
    { label: 'Altro', value: Math.round(totalRevenue * 0.04), color: T.muted2 },
  ];
  const revenueMax = Math.max(...revenueChannels.map((c) => c.value), 1);

  return (
    <div style={{ padding: '20px 24px', background: T.bg, minHeight: '100vh' }}>

      {/* ── SEZIONE 1: Welcome + Urgenze ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr',
          gap: 14,
          marginBottom: 14,
        }}
      >
        {/* Welcome Banner */}
        <div
          style={{
            background: 'linear-gradient(115deg, #0B0B0D 0%, #1A1A1F 60%, #e5252b 180%)',
            padding: '20px 22px',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            color: '#fff',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 1.5,
              color: T.gold,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            CIAO {nome.toUpperCase()} · OGGI {oggi}
          </div>
          <h1
            style={{
              margin: '0 0 4px',
              fontSize: 22,
              fontWeight: 900,
              fontStyle: 'italic',
              letterSpacing: -0.5,
              color: '#fff',
              lineHeight: 1.1,
            }}
          >
            {data.nextMatch
              ? `Prossima partita in ${data.nextMatch.daysUntil} giorni`
              : 'Nessuna partita in programma'}
          </h1>
          {data.nextMatch && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 14 }}>
              {data.nextMatch.homeTeamName} vs {data.nextMatch.awayTeamName}
            </div>
          )}
          {!data.nextMatch && <div style={{ marginBottom: 14 }} />}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <DashBtn variant="primary" icon="◈" onClick={() => navigate('/matches')}>
              GESTISCI EVENTO MATCH
            </DashBtn>
            <DashBtn icon="▲" onClick={() => navigate('/push')}>
              INVIA PUSH PROMO
            </DashBtn>
          </div>
        </div>

        {/* Azioni urgenti */}
        <BOCard title="Azioni urgenti" noPadding>
          {urgentItems.length === 0 ? (
            <div
              style={{
                padding: '24px 14px',
                textAlign: 'center',
                fontSize: 13,
                color: T.green,
                fontWeight: 700,
              }}
            >
              Tutto in ordine ✓
            </div>
          ) : (
            urgentItems.map((item, idx) => (
              <div
                key={idx}
                onClick={() => navigate(item.path)}
                style={{
                  padding: '10px 14px',
                  borderBottom: idx < urgentItems.length - 1 ? `1px solid ${T.line}` : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    background: item.c,
                    color: '#fff',
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontStyle: 'italic',
                    fontWeight: 900,
                  }}
                >
                  {item.n}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: T.ink }}>{item.l}</div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{item.sub}</div>
                </div>
                <div style={{ color: T.muted2, fontSize: 16 }}>→</div>
              </div>
            ))
          )}
        </BOCard>
      </div>

      {/* ── SEZIONE 2: KPI Grid ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 10,
          marginBottom: 14,
        }}
      >
        {kpis.slice(0, 8).map((kpi, i) => (
          <div
            key={i}
            style={{
              background: T.paper,
              border: `1px solid ${T.line}`,
              borderLeft: `3px solid ${kpi.urgent ? T.red : kpi.trend === 'down' ? T.warn : T.ink}`,
              borderRadius: 3,
              padding: 14,
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: 0.8,
                color: T.muted,
                textTransform: 'uppercase',
                marginBottom: 4,
              }}
            >
              {kpi.icon} {kpi.k}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                fontStyle: 'italic',
                color: kpi.urgent ? T.red : T.ink,
                lineHeight: 1.1,
                marginBottom: 4,
              }}
            >
              {kpi.v}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {kpi.delta && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: kpi.trend === 'up' ? T.green : kpi.trend === 'down' ? T.warn : T.muted2,
                  }}
                >
                  {kpi.trend === 'up' ? '↑' : kpi.trend === 'down' ? '↓' : '→'} {kpi.delta}
                </span>
              )}
              <span style={{ fontSize: 10, color: T.muted2 }}>{kpi.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── SEZIONE 3: Grafico + Attività recente ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gap: 14,
          marginBottom: 14,
        }}
      >
        <BOCard title="Crescita utenti · ultimi 30 giorni">
          {growthLoading ? (
            <div
              style={{ height: 200, background: T.line, borderRadius: 3, opacity: 0.5 }}
            />
          ) : growthData && growthData.length > 0 ? (
            <UserGrowthChart data={growthData} />
          ) : (
            <div
              style={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: T.muted2,
              }}
            >
              Nessun dato disponibile
            </div>
          )}
        </BOCard>

        <BOCard title="Attività recente" noPadding>
          {recentOrders.length === 0 ? (
            <div style={{ padding: '24px 14px', fontSize: 12, color: T.muted2, textAlign: 'center' }}>
              Nessuna attività recente
            </div>
          ) : (
            recentOrders.map((order, idx) => {
              const customerName =
                order.customer?.firstName
                  ? `${order.customer.firstName} ${order.customer.lastName ?? ''}`.trim()
                  : order.customer?.email ?? 'Cliente';
              const initials = customerName.substring(0, 2).toUpperCase();
              const color = activityColorMap['orders'];
              const when = dayjs(order.createdAt).format('D MMM HH:mm');
              return (
                <div
                  key={order.id}
                  style={{
                    padding: '10px 14px',
                    borderBottom:
                      idx < recentOrders.length - 1 ? `1px solid ${T.line}` : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: color,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 9,
                      fontWeight: 900,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: T.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {customerName}
                    </div>
                    <div style={{ fontSize: 10, color: T.muted }}>
                      Ordine #{order.id.substring(0, 8)} · € {order.finalAmount.toFixed(2)}
                    </div>
                  </div>
                  <div style={{ fontSize: 9, color: T.muted2, flexShrink: 0 }}>{when}</div>
                </div>
              );
            })
          )}
        </BOCard>
      </div>

      {/* ── SEZIONE 4: Partita + Top sponsor + Ricavi canale ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 1fr',
          gap: 14,
        }}
      >
        {/* Partita in corso / Prossima partita */}
        <BOCard
          title={liveMatch ? 'Partita in corso' : 'Prossima partita'}
          actions={
            liveMatch ? (
              <span style={{ background: '#fde8e6', color: '#e5252b', fontSize: 9, fontWeight: 900, padding: '2px 8px', borderRadius: 3, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#e5252b', animation: 'blLivePulse 1.3s infinite' }} />
                IN DIRETTA
              </span>
            ) : null
          }
        >
          {liveMatch ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.red, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, fontStyle: 'italic', flexShrink: 0 }}>
                  {liveMatch.homeTeamName.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: 0.5 }}>
                    IN DIRETTA
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, fontStyle: 'italic', color: T.ink, letterSpacing: -0.3 }}>
                    VS
                  </div>
                </div>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.ink2, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, fontStyle: 'italic', flexShrink: 0 }}>
                  {liveMatch.awayTeamName.charAt(0).toUpperCase()}
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, textAlign: 'center', marginBottom: 4 }}>
                {liveMatch.homeTeamName} — {liveMatch.awayTeamName}
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, fontStyle: 'italic', letterSpacing: -1, color: '#e5252b', textAlign: 'center', margin: '8px 0' }}>
                {liveMatch.homeScore ?? 0} – {liveMatch.awayScore ?? 0}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, paddingTop: 12, borderTop: `1px solid ${T.line}` }}>
                {[
                  { label: 'Check-in attesi', value: '—' },
                  { label: 'Push previste', value: '—' },
                  { label: 'Biglietti', value: '—' },
                ].map((stat) => (
                  <div key={stat.label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 900, fontStyle: 'italic', color: T.ink }}>{stat.value}</div>
                    <div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : data.nextMatch ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: data.nextMatch.homeTeamLogo ? '#fff' : T.red,
                    border: data.nextMatch.homeTeamLogo ? `1px solid ${T.line}` : undefined,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {data.nextMatch.homeTeamLogo ? (
                    <img
                      src={data.nextMatch.homeTeamLogo}
                      alt={data.nextMatch.homeTeamName}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    data.nextMatch.homeTeamName.charAt(0).toUpperCase()
                  )}
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, letterSpacing: 0.5 }}>
                    {dayjs(data.nextMatch.date).format('ddd D MMM · HH:mm').toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 900,
                      fontStyle: 'italic',
                      color: T.ink,
                      letterSpacing: -0.3,
                    }}
                  >
                    VS
                  </div>
                  <BOBadge tone="red">Tra {data.nextMatch.daysUntil} giorni</BOBadge>
                </div>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: data.nextMatch.awayTeamLogo ? '#fff' : T.ink2,
                    border: data.nextMatch.awayTeamLogo ? `1px solid ${T.line}` : undefined,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    flexShrink: 0,
                    overflow: 'hidden',
                  }}
                >
                  {data.nextMatch.awayTeamLogo ? (
                    <img
                      src={data.nextMatch.awayTeamLogo}
                      alt={data.nextMatch.awayTeamName}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    data.nextMatch.awayTeamName.charAt(0).toUpperCase()
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.ink, textAlign: 'center', marginBottom: 12 }}>
                {data.nextMatch.homeTeamName} — {data.nextMatch.awayTeamName}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 8,
                  paddingTop: 12,
                  borderTop: `1px solid ${T.line}`,
                }}
              >
                {[
                  { label: 'Check-in attesi', value: '—' },
                  { label: 'Push previste', value: '—' },
                  { label: 'Biglietti', value: '—' },
                ].map((stat) => (
                  <div key={stat.label} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 900, fontStyle: 'italic', color: T.ink }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: 9, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                padding: '24px 0',
                textAlign: 'center',
                fontSize: 13,
                color: T.muted2,
              }}
            >
              Nessuna partita programmata
            </div>
          )}
        </BOCard>

        {/* Top sponsor loyalty — ranking per score (scontrini + valore) */}
        <BOCard title="Top Sponsor Loyalty" noPadding>
          {data.topSponsors.length === 0 ? (
            <div style={{ padding: '24px 14px', textAlign: 'center', fontSize: 12, color: T.muted2 }}>
              Nessuno scontrino approvato risulta collegato a un partner
            </div>
          ) : (
            data.topSponsors.slice(0, 5).map((sponsor, idx) => (
              <div
                key={sponsor.sponsorId}
                style={{
                  padding: '10px 14px',
                  borderBottom:
                    idx < Math.min(data.topSponsors.length, 5) - 1
                      ? `1px solid ${T.line}`
                      : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 20,
                    fontSize: 11,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    color: idx === 0 ? T.gold : T.muted2,
                    flexShrink: 0,
                  }}
                >
                  #{idx + 1}
                </div>
                <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.ink }}>
                  {sponsor.name}
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.muted }}>
                    {sponsor.receiptCount} scontrini · €{sponsor.totalValue.toFixed(0)}
                  </div>
                </div>
                <BOBadge tone="gold">{sponsor.score.toFixed(1)} pt</BOBadge>
              </div>
            ))
          )}
        </BOCard>

        {/* Ricavi per canale */}
        <BOCard title="Ricavi per canale">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {revenueChannels.map((ch) => {
              const pct = revenueMax > 0 ? (ch.value / revenueMax) * 100 : 0;
              return (
                <div key={ch.label}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.ink }}>{ch.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 800, color: T.muted }}>
                      € {ch.value.toLocaleString('it')}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 6,
                      background: T.line2,
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: ch.color,
                        borderRadius: 3,
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </BOCard>
      </div>
    </div>
  );
}
